/**
 * localMetricsProducts.ts — Rankings de productos, equipo y rentabilidad local.
 *
 * QUÉ HACE: calcula top productos, desempeño por empleado, distribución por
 *   categoría (donut + top productos) y margen bruto del periodo desde LS,
 *   resolviendo categoría/costo contra inventory_products.
 *
 * QUÉ INVOCA: txRead (transactionalStore), defaultProducts (mocks), date-fns y
 *   helpers de localMetricsShared.
 *
 * QUÉ EXPORTA: computeLocalTopProducts, computeLocalTeamPerformance,
 *   LocalCategoryData, computeLocalCategoryData, computeLocalMarginData.
 *
 * QUÉ NO DEBE HACER: UI ni HTTP.
 */
import { txRead } from "@/lib/persistence/transactionalStore";
import { defaultProducts } from "@/mocks/data/products";
import {
  isSameDay,
  getHours,
  getMonth,
  getYear,
  startOfDay,
  startOfMonth,
  subDays,
} from "date-fns";
import type {
  EmployeePerformance,
  MarginData,
  MarginProduct,
  MarginPeriod,
  MetricsPeriod,
  TopProduct,
} from "../types/metrics.types";
import {
  categoryColor,
  peakHourCutoff,
  readVentasLocal,
  ventaFecha,
  ventasActivas,
  ventasCutoffForPeriod,
  type DonutPeriodLike,
  type DrillLevel,
} from "./localMetricsShared";

export function computeLocalTopProducts(limit = 5): TopProduct[] {
  const ventas = ventasActivas(readVentasLocal());
  /** Categoría por producto, resuelta contra inventory_products. */
  const productos = txRead("inventory_products", []) as Array<{
    id: string;
    categoria?: string;
  }>;
  const categoriaPorId = new Map(
    productos.map((p) => [p.id, p.categoria || "Otros"]),
  );
  const counts: Record<
    string,
    { nombre: string; cantidad: number; total: number }
  > = {};

  ventas.forEach((v) => {
    (v.productos || []).forEach((p) => {
      const baseId = p.productoId || p.nombre || "unknown";
      const mods = p.modificadoresSeleccionados ?? [];
      const id =
        mods.length > 0
          ? `${baseId}#${
              p.modifierKey ??
              mods
                .map((m) => m.optionId)
                .sort()
                .join("")
            }`
          : baseId;

      if (!counts[id]) {
        const optionLabels = mods.map((m) => m.optionLabel);
        counts[id] = {
          nombre:
            optionLabels.length > 0
              ? `${p.nombre || baseId} — ${optionLabels.join(", ")}`
              : p.nombre || baseId,
          cantidad: 0,
          total: 0,
        };
      }
      const qty = Number(p.cantidad) || 1;
      counts[id].cantidad += qty;
      // El subtotal no siempre viene persistido; usamos precioUnitario × cantidad.
      counts[id].total += qty * (Number(p.precioUnitario) || 0);
    });
  });

  return Object.entries(counts)
    .map(([id, data]) => ({
      id,
      nombre: data.nombre,
      cantidadVendida: data.cantidad,
      totalIngresos: data.total,
      categoria: categoriaPorId.get(id.split("#")[0]) ?? "Otros",
    }))
    .sort((a, b) => b.totalIngresos - a.totalIngresos)
    .slice(0, limit);
}

export function computeLocalTeamPerformance(
  period: MetricsPeriod,
): EmployeePerformance[] {
  const ventas = ventasActivas(readVentasLocal());
  const now = new Date();
  let start = startOfDay(now);
  if (period === "week") start = subDays(now, 7);
  else if (period === "month") start = startOfMonth(now);

  const filtered = ventas.filter((v) => ventaFecha(v) >= start);
  const byEmployee: Record<
    string,
    { nombre: string; ventas: number; total: number }
  > = {};

  filtered.forEach((v) => {
    const nombre = v.empleadoNombre || v.userName || "Sin asignar";
    if (!byEmployee[nombre]) {
      byEmployee[nombre] = { nombre, ventas: 0, total: 0 };
    }
    byEmployee[nombre].ventas += 1;
    byEmployee[nombre].total += v.total || 0;
  });

  return Object.values(byEmployee).map((e) => ({
    empleadoId: e.nombre,
    nombre: e.nombre,
    cantidadVentas: e.ventas,
    totalVentas: e.total,
  }));
}

/** Estructura de salida compatible con CATEGORY_PERIOD_DATA de categoryMockData.ts */
export interface LocalCategoryData {
  categories: Array<{ name: string; value: number; color: string }>;
  topProducts: TopProduct[];
}

/**
 * Calcula distribución de ventas por categoría desde localStorage.
 * Lee inventory_products para resolver productoId → categoria.
 * Retorna datos compatibles con CATEGORY_PERIOD_DATA / generateCategoryDataForDate.
 */
export function computeLocalCategoryData(
  period: DonutPeriodLike,
  forDate?: Date,
  level?: DrillLevel,
): LocalCategoryData {
  const productos = txRead("inventory_products", []) as Array<{
    id: string;
    nombre?: string;
    categoria?: string;
  }>;
  const productoMap: Record<string, string> = {};
  productos.forEach((p) => {
    productoMap[p.id] = p.categoria || "Otros";
  });

  // Filtro temporal
  let ventas = ventasActivas(readVentasLocal());
  if (forDate && level) {
    ventas = ventas.filter((v) => {
      const fd = ventaFecha(v);
      if (level === "hora") {
        return isSameDay(fd, forDate) && getHours(fd) === getHours(forDate);
      }
      if (level === "hoy") return isSameDay(fd, forDate);
      if (level === "mes")
        return (
          getMonth(fd) === getMonth(forDate) && getYear(fd) === getYear(forDate)
        );
      // anio
      return getYear(fd) === getYear(forDate);
    });
  } else {
    const cutoff = ventasCutoffForPeriod(period);
    ventas = ventas.filter((v) => ventaFecha(v) >= cutoff);
  }

  // Acumular por categoría: cantidad y total ingresos
  const catTotals: Record<
    string,
    {
      cantidad: number;
      ingresos: number;
      productos: Record<
        string,
        { nombre: string; cantidad: number; ingresos: number }
      >;
    }
  > = {};

  ventas.forEach((v) => {
    const prods =
      (
        v as unknown as {
          productos?: Array<{
            productoId: string;
            nombre: string;
            cantidad: number;
            precioUnitario: number;
            modificadoresSeleccionados?: {
              optionId: string;
              optionLabel: string;
            }[];
            modifierKey?: string;
          }>;
        }
      ).productos || [];
    prods.forEach((p) => {
      const cat = productoMap[p.productoId] || "Otros";
      if (!catTotals[cat]) {
        catTotals[cat] = { cantidad: 0, ingresos: 0, productos: {} };
      }
      const qty = Number(p.cantidad) || 1;
      const lineTotal = qty * (Number(p.precioUnitario) || 0);
      catTotals[cat].cantidad += qty;
      catTotals[cat].ingresos += lineTotal;

      const mods = p.modificadoresSeleccionados ?? [];
      const pid =
        mods.length > 0
          ? `${p.productoId || p.nombre}#${
              p.modifierKey ??
              mods
                .map((m) => m.optionId)
                .sort()
                .join("")
            }`
          : p.productoId || p.nombre;
      if (!catTotals[cat].productos[pid]) {
        const optionLabels = mods.map((m) => m.optionLabel);
        catTotals[cat].productos[pid] = {
          nombre:
            optionLabels.length > 0
              ? `${p.nombre || pid} — ${optionLabels.join(", ")}`
              : p.nombre || pid,
          cantidad: 0,
          ingresos: 0,
        };
      }
      catTotals[cat].productos[pid].cantidad += qty;
      catTotals[cat].productos[pid].ingresos += lineTotal;
    });
  });

  // Si no hay datos granulares → usar total de la venta sin desgloses de producto
  if (Object.keys(catTotals).length === 0 && ventas.length > 0) {
    catTotals["Ventas"] = {
      cantidad: ventas.length,
      ingresos: ventas.reduce((s, v) => s + (v.total || 0), 0),
      productos: {},
    };
  }

  const catEntries = Object.entries(catTotals).sort(
    (a, b) => b[1].cantidad - a[1].cantidad,
  );

  const categories = catEntries.map(([name, data], idx) => ({
    name,
    value: data.cantidad,
    color: categoryColor(name, idx),
  }));

  // Top productos: todos los productos de todas las categorías, ordenados por ingresos
  const allProds: TopProduct[] = [];
  catEntries.forEach(([catName, catData]) => {
    Object.entries(catData.productos)
      .sort((a, b) => b[1].ingresos - a[1].ingresos)
      .forEach(([pid, pd]) => {
        allProds.push({
          id: pid,
          nombre: pd.nombre,
          cantidadVendida: pd.cantidad,
          totalIngresos: pd.ingresos,
          categoria: catName,
        });
      });
  });
  // Deduplicar y ordenar por ingresos
  const seen = new Set<string>();
  const topProducts = allProds
    .filter((p) => {
      if (seen.has(p.id)) return false;
      seen.add(p.id);
      return true;
    })
    .sort((a, b) => b.totalIngresos - a.totalIngresos);

  return { categories, topProducts };
}

/* ── Rentabilidad real (margen bruto) ────────────────────────────────────── */

/**
 * Calcula el margen bruto del periodo desde caja_ventas_historial, resolviendo
 * el costo de cada producto contra inventory_products.
 *
 * Reglas:
 *   - margen = ingresos (precio × cantidad) − costo (costo × cantidad).
 *   - Productos manuales sin `productoId` o sin costo conocido → costo 0.
 *   - Devuelve totales, margen por categoría y top productos por margen.
 */
export function computeLocalMarginData(period: MarginPeriod): MarginData {
  const cutoff = peakHourCutoff(period);
  const ventas = ventasActivas(readVentasLocal()).filter(
    (v) => ventaFecha(v) >= cutoff,
  );

  const productos = txRead("inventory_products", defaultProducts) as Array<{
    id: string;
    nombre?: string;
    categoria?: string;
    costo?: number;
  }>;
  const productoMap = new Map(productos.map((p) => [p.id, p]));

  const prodAcc = new Map<string, MarginProduct>();
  const catAcc = new Map<string, number>();
  let ingresos = 0;
  let costo = 0;

  ventas.forEach((v) => {
    (v.productos ?? []).forEach((line) => {
      const qty = Number(line.cantidad) || 1;
      const lineIngreso =
        Number(line.subtotal) || qty * (Number(line.precioUnitario) || 0);
      const prod = line.productoId
        ? productoMap.get(line.productoId)
        : undefined;
      const lineCosto = (Number(prod?.costo) || 0) * qty;
      const lineMargen = lineIngreso - lineCosto;
      const nombre = line.nombre || prod?.nombre || "Producto";
      const categoria = prod?.categoria || "Otros";
      const key = line.productoId || nombre;

      const prev = prodAcc.get(key);
      if (prev) {
        prev.cantidadVendida += qty;
        prev.totalIngresos += lineIngreso;
        prev.costo += lineCosto;
        prev.margen += lineMargen;
      } else {
        prodAcc.set(key, {
          id: key,
          nombre,
          categoria,
          cantidadVendida: qty,
          totalIngresos: lineIngreso,
          costo: lineCosto,
          margen: lineMargen,
        });
      }

      ingresos += lineIngreso;
      costo += lineCosto;
      catAcc.set(categoria, (catAcc.get(categoria) ?? 0) + lineMargen);
    });
  });

  // Incluir TODO el inventario: los productos sin ventas en el periodo quedan en $0.
  productos.forEach((p) => {
    if (prodAcc.has(p.id)) return;
    prodAcc.set(p.id, {
      id: p.id,
      nombre: p.nombre ?? p.id,
      categoria: p.categoria || "Otros",
      cantidadVendida: 0,
      totalIngresos: 0,
      costo: 0,
      margen: 0,
    });
  });

  const margen = ingresos - costo;
  const porCategoria = Array.from(catAcc.entries())
    .filter(([, value]) => value > 0)
    .sort((a, b) => b[1] - a[1])
    .map(([name, value], idx) => ({
      name,
      value,
      color: categoryColor(name, idx),
    }));
  const topProductos = Array.from(prodAcc.values()).sort(
    (a, b) => b.margen - a.margen,
  );

  return {
    ingresos,
    costo,
    margen,
    margenPct: ingresos > 0 ? (margen / ingresos) * 100 : 0,
    porCategoria,
    topProductos,
  };
}
